/**
 * Página pública /coreanos — catálogo independiente de productos coreanos
 * con cotización (armar pedido). El total se calcula automáticamente por
 * bracket de cantidad (unitario / medio mayoreo / mayoreo) y se envía a
 * WhatsApp de Rozette al presionar "Enviar pedido".
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PublicToolsMenu from '../components/public/PublicToolsMenu';
import { ProductImageCarousel, ProductInfoButton } from '../components/public/ProductMediaCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROZETTE_PHONE = '8441136858';
const ML_URL = 'https://listado.mercadolibre.com.mx/pagina/rozetteplastics/listado/alimentos-bebidas/?tracking_id=fdd8f58a5abac4c9d0b5006620f297df&skipInApp=true&matt_ignore=true';

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

function normalizePhoneWa(p) {
  const clean = String(p || '').replace(/[^\d+]/g, '');
  if (!clean) return '';
  if (clean.startsWith('+')) return clean.slice(1);
  if (clean.length === 10) return `52${clean}`;
  return clean;
}

/** Devuelve el precio unitario según el bracket que aplique para `qty`.
 * Si `qty` viene indefinida se usa `item.__qty` (fallback). El caller es
 * responsable de pasar la cantidad EFECTIVA: para productos con categoría,
 * es la SUMA total de la categoría en el carrito (para permitir combinar
 * sabores). Para productos sin categoría se usa la qty del producto solo.
 *
 * En variante 'envios': se ignora `mayoreo_price` y se usa `shipment_price`
 * como el precio activo cuando qty >= 1 (si está definido). No hay umbral.
 */
function priceForQty(item, qty, variant = 'default') {
  const q = Number(qty) || 0;
  if (q <= 0) return Number(item.unit_price) || 0;
  if (variant === 'envios') {
    // Solo 2 niveles: 1 pieza (unit_price) o Envío (shipment_price) cuando qty >= shipment_qty (default 1).
    if (item.shipment_price && Number(item.shipment_price) > 0) {
      const minQty = Number(item.shipment_qty) || 1;
      if (q >= minQty) return Number(item.shipment_price);
    }
    return Number(item.unit_price) || 0;
  }
  if (item.mayoreo_qty && item.mayoreo_price && q >= item.mayoreo_qty) {
    return Number(item.mayoreo_price);
  }
  if (item.medio_mayoreo_qty && item.medio_mayoreo_price && q >= item.medio_mayoreo_qty) {
    return Number(item.medio_mayoreo_price);
  }
  return Number(item.unit_price) || 0;
}

/** Etiqueta corta del bracket actual, útil para mostrar debajo del subtotal. */
function bracketLabel(item, qty, variant = 'default') {
  const q = Number(qty) || 0;
  if (variant === 'envios') {
    if (item.shipment_price && Number(item.shipment_price) > 0) {
      const minQty = Number(item.shipment_qty) || 1;
      if (q >= minQty) return '📦 Precio envío';
    }
    return null;
  }
  if (item.mayoreo_qty && item.mayoreo_price && q >= item.mayoreo_qty) return '🟢 Mayoreo';
  if (item.medio_mayoreo_qty && item.medio_mayoreo_price && q >= item.medio_mayoreo_qty) return '🟠 Medio mayoreo';
  return null;
}

function QtyStepper({ qty, onChange, testid }) {
  const dec = () => onChange(Math.max(0, (qty || 0) - 1));
  const inc = () => onChange((qty || 0) + 1);
  return (
    <div className="inline-flex items-center rounded-lg border border-slate-300 overflow-hidden bg-white">
      <button type="button" onClick={dec} className="w-8 h-8 text-slate-700 hover:bg-slate-100 font-bold" data-testid={`${testid}-dec`} aria-label="Restar">−</button>
      <input
        type="number"
        inputMode="numeric"
        min="0"
        value={qty || 0}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || '0', 10) || 0))}
        className="w-12 h-8 text-center border-x border-slate-200 tabular-nums text-sm outline-none"
        data-testid={testid}
      />
      <button type="button" onClick={inc} className="w-8 h-8 text-slate-700 hover:bg-slate-100 font-bold" data-testid={`${testid}-inc`} aria-label="Sumar">+</button>
    </div>
  );
}

function KoreanCard({ item, priority, qty, setQty, effectiveQty, variant = 'default' }) {
  const isEnv = variant === 'envios';
  const hasMedio = !isEnv && item.medio_mayoreo_qty && item.medio_mayoreo_price;
  const hasMayoreo = !isEnv && item.mayoreo_qty && item.mayoreo_price;
  const hasShip = isEnv && item.shipment_price && Number(item.shipment_price) > 0;
  const shipMinQty = Number(item.shipment_qty) || 1;
  const currentPrice = priceForQty(item, effectiveQty, variant);
  const subtotal = currentPrice * (qty || 0);
  const bracket = bracketLabel(item, effectiveQty, variant);

  return (
    <article className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition" data-testid={`korean-card-${item.id}`}>
      <ProductImageCarousel
        mainImage={item.image_url}
        extraImages={[]}
        alt={item.name}
        fallbackEmoji="🥟"
        priority={priority}
      >
        <ProductInfoButton productName={item.name} info={item.product_info} sku={item.sku} purchaseCost={item.purchase_cost} stockM={item.stock_m} stockA={item.stock_a} />
      </ProductImageCarousel>

      <div className="p-3 flex flex-col gap-2 flex-1">
        <h3 className="text-sm font-semibold text-slate-800 leading-tight line-clamp-2">{item.name}</h3>

        <div className="bg-slate-50 border border-slate-200 rounded-lg divide-y divide-slate-200 text-xs">
          <div className="flex items-center justify-between px-2.5 py-1.5">
            <span className="text-slate-600">Desde 1 pieza</span>
            <span className="font-bold text-slate-900 tabular-nums">{money(item.unit_price)}</span>
          </div>
          {hasMedio && (
            <div className="flex items-center justify-between px-2.5 py-1.5 bg-amber-50">
              <span className="text-amber-800">Desde {item.medio_mayoreo_qty} pzs</span>
              <span className="font-bold text-amber-900 tabular-nums">{money(item.medio_mayoreo_price)}</span>
            </div>
          )}
          {hasMayoreo && (
            <div className="flex items-center justify-between px-2.5 py-1.5 bg-emerald-50">
              <span className="text-emerald-800">Desde {item.mayoreo_qty} pzs</span>
              <span className="font-bold text-emerald-900 tabular-nums">{money(item.mayoreo_price)}</span>
            </div>
          )}
          {hasShip && (
            <div className="flex items-center justify-between px-2.5 py-1.5 bg-sky-50">
              <span className="text-sky-800">📦 Desde {shipMinQty} pz{shipMinQty === 1 ? '' : 's'} (envío)</span>
              <span className="font-bold text-sky-900 tabular-nums">{money(item.shipment_price)}</span>
            </div>
          )}
        </div>

        <div className="mt-auto pt-2 space-y-2">
          <div className="flex items-center justify-between">
            <QtyStepper qty={qty} onChange={setQty} testid={`korean-qty-${item.id}`} />
            {qty > 0 && (
              <div className="text-right">
                <div className="text-sm font-bold text-slate-900 tabular-nums" data-testid={`korean-subtotal-${item.id}`}>{money(subtotal)}</div>
                {bracket && <div className="text-[10px] text-slate-500">{bracket}</div>}
              </div>
            )}
          </div>
          {variant !== 'envios' && item.ml_url && (
            <a
              href={item.ml_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-[11px] font-semibold py-1.5 px-2.5 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-slate-900 transition flex items-center justify-center gap-1"
              data-testid={`korean-ml-item-${item.id}`}
            >🛒 Mercado Libre · Envío gratis</a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function KoreanProducts({ variant = 'default' }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedCat, setSelectedCat] = useState('all');
  const [cart, setCart] = useState({}); // { [product_id]: qty }
  const [cartOpen, setCartOpen] = useState(false);
  const [showQuoteHelp, setShowQuoteHelp] = useState(false);
  const [showWholesaleHelp, setShowWholesaleHelp] = useState(false);
  const isEnvios = variant === 'envios';

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const { data } = await axios.get(`${API}/public/korean-products`);
        if (!cancel) setItems(data.items || []);
      } catch (e) {
        if (!cancel) setError('No se pudo cargar el catálogo');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const categories = useMemo(() => {
    if (!items) return [];
    const uniq = new Set(items.map(i => (i.category || '').trim()).filter(Boolean));
    return Array.from(uniq).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    let list = items;
    if (selectedCat !== 'all') {
      if (selectedCat === '__uncategorized__') list = list.filter(i => !i.category);
      else list = list.filter(i => (i.category || '') === selectedCat);
    }
    if (q) list = list.filter(i => (i.name || '').toLowerCase().includes(q));
    return list;
  }, [items, query, selectedCat]);

  const setQty = (id, qty) => {
    setCart(prev => {
      const next = { ...prev };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });
  };

  // Cantidad total por categoría en el carrito. Se usa para calcular el
  // bracket combinando sabores/variantes de una misma categoría.
  const qtyByCategory = useMemo(() => {
    if (!items) return {};
    const map = {};
    for (const [id, q] of Object.entries(cart)) {
      const it = items.find(x => x.id === id);
      if (!it) continue;
      const key = it.category || `__uncat__${id}`; // sin categoría → cada uno cuenta solo
      map[key] = (map[key] || 0) + q;
    }
    return map;
  }, [cart, items]);

  const effectiveQtyFor = (item) => {
    if (item.category) return qtyByCategory[item.category] || (cart[item.id] || 0);
    return cart[item.id] || 0;
  };

  // Totales del carrito
  const cartItems = useMemo(() => {
    if (!items) return [];
    return Object.entries(cart)
      .map(([id, qty]) => {
        const it = items.find(x => x.id === id);
        if (!it) return null;
        const eff = it.category ? (qtyByCategory[it.category] || qty) : qty;
        const unit = priceForQty(it, eff, variant);
        return { ...it, qty, unit, subtotal: unit * qty, bracket: bracketLabel(it, eff, variant) };
      })
      .filter(Boolean);
  }, [cart, items, qtyByCategory, variant]);

  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.subtotal, 0);

  const sendOrderWhatsApp = () => {
    if (cartItems.length === 0) return;
    const lines = cartItems.map(i => `• ${i.qty}× ${i.name} — ${money(i.unit)} c/u = ${money(i.subtotal)}${i.bracket ? ` (${i.bracket})` : ''}`);
    const msg = `¡Hola Rozette! Quiero armar un pedido:\n\n${lines.join('\n')}\n\n*Total: ${money(cartTotal)}*\n\n¿Confirmamos disponibilidad?`;
    const waUrl = `https://wa.me/${normalizePhoneWa(ROZETTE_PHONE)}?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {!isEnvios && <PublicToolsMenu current="/coreanos" />}

      <header
        className="bg-gradient-to-br from-red-700 via-red-800 to-slate-900 text-white shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
      >
        <div className="max-w-5xl mx-auto px-4 pb-6">
          <div className="text-center">
            <p className="text-[11px] uppercase tracking-widest opacity-80">Rozette · Productos coreanos</p>
            <h1 className="text-3xl sm:text-4xl font-bold mt-1">🥟 Botanas y bebidas coreanas</h1>
          </div>

          <div className="mt-4 bg-white/10 border border-white/20 rounded-2xl p-3 sm:p-4 text-center backdrop-blur-sm">
            <p className="text-sm sm:text-base font-semibold">
              📍 Entregas inmediatas en <span className="text-amber-300">Saltillo</span> en nuestra sucursal
            </p>
            <p className="text-xs sm:text-sm opacity-90 mt-1">
              {isEnvios
                ? '🇲🇽 Envíos a toda la república mexicana'
                : '🇲🇽 Ventas a toda la República a través de Mercado Libre'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              <a
                href={`https://wa.me/${normalizePhoneWa(ROZETTE_PHONE)}?text=${encodeURIComponent('¡Hola Rozette! Me interesa la línea coreana.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-full text-sm shadow transition"
                data-testid="korean-contact-wa-btn"
              >💬 Contactar por WhatsApp</a>
              <button
                type="button"
                onClick={() => setShowQuoteHelp(true)}
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-red-700 font-semibold px-4 py-2 rounded-full text-sm shadow transition"
                data-testid="korean-quote-wa-btn"
              >🧾 Cotizar por WhatsApp</button>
              {!isEnvios && (
                <a
                  href={ML_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold px-4 py-2 rounded-full text-sm shadow transition"
                  data-testid="korean-ml-btn"
                >🛒 Mercado Libre · Envío gratis</a>
              )}
              {!isEnvios && (
                <button
                  type="button"
                  onClick={() => setShowWholesaleHelp(true)}
                  className="inline-flex items-center gap-2 bg-slate-800/60 hover:bg-slate-800/80 text-white font-semibold px-4 py-2 rounded-full text-sm shadow border border-white/20 transition"
                  data-testid="korean-wholesale-info-btn"
                >🤝 Info mayoristas</button>
              )}
            </div>
          </div>

          <div className="mt-4">
            <input
              type="search"
              placeholder="🔍 Buscar por nombre…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-white/95 text-slate-900 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="korean-search"
            />
          </div>

          {/* Chips de categorías */}
          {categories.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="korean-cats">
              <button
                onClick={() => setSelectedCat('all')}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition ${selectedCat === 'all' ? 'bg-white text-red-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
                data-testid="korean-cat-all"
              >Todos ({items?.length || 0})</button>
              {categories.map((c) => {
                const n = items.filter(i => i.category === c).length;
                return (
                  <button
                    key={c}
                    onClick={() => setSelectedCat(c)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${selectedCat === c ? 'bg-white text-red-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    data-testid={`korean-cat-${c}`}
                  >{c} ({n})</button>
                );
              })}
              {items?.some(i => !i.category) && (
                <button
                  onClick={() => setSelectedCat('__uncategorized__')}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition ${selectedCat === '__uncategorized__' ? 'bg-white text-red-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
                >Sin categoría</button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 pb-28">
        {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm mb-4">{error}</div>}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        )}
        {!loading && filtered.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="text-6xl mb-3">🥟</div>
            <p className="text-slate-500">
              {items && items.length === 0 ? 'Aún no hay productos coreanos registrados.' : 'Sin resultados.'}
            </p>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="korean-grid">
            {filtered.map((it, i) => (
              <KoreanCard
                key={it.id}
                item={it}
                priority={i < 8}
                qty={cart[it.id] || 0}
                effectiveQty={effectiveQtyFor(it)}
                setQty={(q) => setQty(it.id, q)}
                variant={variant}
              />
            ))}
          </div>
        )}
      </main>

      {/* Barra flotante del carrito */}
      {cartCount > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed z-40 bottom-5 right-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-2xl px-5 py-3.5 flex items-center gap-2 font-semibold text-sm transition"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
          data-testid="korean-cart-open"
        >
          <span className="text-lg leading-none">🛒</span>
          <span>Armar pedido · {cartCount} pzs</span>
          <span className="ml-1 bg-white text-emerald-700 rounded-full text-xs font-black px-2 py-0.5">{money(cartTotal)}</span>
        </button>
      )}

      {/* Sheet del carrito */}
      {cartOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setCartOpen(false)}
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="korean-cart-sheet"
          >
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="font-bold text-slate-900">🛒 Tu pedido</h2>
                <p className="text-xs text-slate-500">{cartCount} pzs · Total: {money(cartTotal)}</p>
              </div>
              <button onClick={() => setCartOpen(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500" aria-label="Cerrar">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {cartItems.map((i) => (
                <div key={i.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg">
                  {i.image_url ? (
                    <img src={i.image_url} alt={i.name} loading="lazy" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-lg">🥟</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{i.name}</p>
                    <p className="text-xs text-slate-500">{i.qty} × {money(i.unit)}{i.bracket ? ` · ${i.bracket}` : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold tabular-nums">{money(i.subtotal)}</p>
                    <QtyStepper qty={i.qty} onChange={(q) => setQty(i.id, q)} testid={`cart-qty-${i.id}`} />
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-500">Total</span>
                <span className="text-2xl font-black text-slate-900 tabular-nums">{money(cartTotal)}</span>
              </div>
              <button
                onClick={sendOrderWhatsApp}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition"
                data-testid="korean-cart-send-wa"
              >
                💬 Enviar pedido por WhatsApp
              </button>
              <button
                onClick={() => { setCart({}); setCartOpen(false); }}
                className="w-full text-xs text-slate-500 hover:text-slate-700"
              >Vaciar carrito</button>
            </div>
          </div>
        </div>
      )}

      <footer className="text-center text-[11px] text-slate-400 py-6">Rozette · Línea coreana</footer>

      {/* Modal instructivo: "Cotizar por WhatsApp" */}
      {showQuoteHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setShowQuoteHelp(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="korean-quote-help"
          >
            <div className="text-center">
              <div className="text-4xl mb-2">🧾</div>
              <h2 className="font-bold text-lg text-slate-900">Cotizar por WhatsApp</h2>
            </div>
            <ol className="mt-4 space-y-3 text-sm text-slate-700">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>Agrega los productos a tu carrito con el botón <b>+</b> en cada tarjeta.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>Ábrelo tocando <b className="text-emerald-700">🛒 Armar pedido</b> en la esquina inferior derecha.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>Pica en <b className="text-emerald-700">💬 Enviar pedido por WhatsApp</b>.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">4</span>
                <span>Nos mandarás tu pedido y con eso realizaremos tu <b>cotización</b> y agendaremos tu <b>entrega</b>. 📦</span>
              </li>
            </ol>
            <button
              onClick={() => setShowQuoteHelp(false)}
              className="mt-5 w-full py-2.5 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 transition"
              data-testid="korean-quote-help-close"
            >Entendido</button>
          </div>
        </div>
      )}

      {/* Modal informativo: "Información mayoristas" */}
      {showWholesaleHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setShowWholesaleHelp(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="korean-wholesale-help"
          >
            <div className="text-center">
              <div className="text-4xl mb-2">🤝</div>
              <h2 className="font-bold text-lg text-slate-900">Info para mayoristas</h2>
              <p className="text-xs text-slate-500 mt-1">Aprovecha el mejor precio combinando sabores</p>
            </div>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              <p>
                <b>Puedes mezclar los sabores del mismo producto</b> hasta alcanzar la cantidad
                mínima del medio mayoreo o mayoreo.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-emerald-900 mb-1">Ejemplo</p>
                <p className="text-xs text-emerald-800">
                  Si el mayoreo de <b>Buldak 140gr</b> se activa a las <b>40 piezas</b>, puedes armar
                  las 40 combinando los sabores que quieras (10 Original + 10 Cheese + 10 Curry
                  + 10 Habanero = 40) y automáticamente todos tomarán el precio de mayoreo. 🎉
                </p>
              </div>
              <p className="text-xs">
                Aplica igual para el <b>medio mayoreo</b>. El sistema calcula el bracket
                automáticamente al ir agregando productos al carrito.
              </p>
              <p className="text-xs text-slate-500">
                ¿Dudas? Da clic en <b>💬 Contactar por WhatsApp</b> arriba y con gusto te apoyamos.
              </p>
            </div>
            <button
              onClick={() => setShowWholesaleHelp(false)}
              className="mt-5 w-full py-2.5 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 transition"
              data-testid="korean-wholesale-help-close"
            >Entendido</button>
          </div>
        </div>
      )}
    </div>
  );
}
